import React, { useState } from 'react';
import { X, ArrowUpRight, CheckCircle2, AlertCircle, Activity } from 'lucide-react';
import { submitEnergyAudit } from '../services/firestoreService';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function EnergyAuditModal({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const [org, setOrg] = useState({ name: '', city: '', lat: '', lng: '', products: '', plantCapacity: '', sanctionedDemand: '', connectedLoad: '', peakDemand: '', lightingLoad: '', lightingVoltage: '', separateFeeder: 'No', apfcInstalled: 'No' });
  const [team, setTeam] = useState([{ name: '', designation: '', mobile: '', email: '' }]);
  const [elecBills, setElecBills] = useState(MONTHS.map(m => ({ month: m, pf: '', kwPurchased: '', kwGenerated: '', consumption: '' })));
  const [elecRate, setElecRate] = useState({ ratePerKwh: '', totalAnnualCost: '' });
  const [thermalBills, setThermalBills] = useState(MONTHS.map(m => ({ month: m, year: '', fuelPower: '', fuelOther: '', consumption: '' })));
  const [thermalRate, setThermalRate] = useState({ ratePerKwh: '', totalAnnualCost: '' });
  const [transformers, setTransformers] = useState([{ make: '', type: '', kvaRating: '', loading: '' }]);
  const [elecEquip, setElecEquip] = useState([{ equipment: '', specs: '', qty: '', watts: '', hrsDay: '', daysYear: '' }]);
  const [thermalEquip, setThermalEquip] = useState([{ equipment: '', specs: '', qty: '', watts: '', hrsDay: '', daysYear: '' }]);
  const [pastAudit, setPastAudit] = useState({ everDone: 'No', audits: [{ year: '', type: '', agency: '' }] });
  const [savingProjects, setSavingProjects] = useState([{ status: '', brief: '', savings: '' }]);
  const [futureAudit, setFutureAudit] = useState({ year: '', month: '', dates: '' });
  const [concerns, setConcerns] = useState('');
  const [declaration, setDeclaration] = useState({ date: '', name: '', designation: '' });

  const addTeam = () => setTeam(t => [...t, { name: '', designation: '', mobile: '', email: '' }]);
  const updateTeam = (i, k, v) => setTeam(t => t.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const updateElecBill = (i, k, v) => setElecBills(b => b.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const updateThermalBill = (i, k, v) => setThermalBills(b => b.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const addTransformer = () => setTransformers(t => [...t, { make: '', type: '', kvaRating: '', loading: '' }]);
  const updateTransformer = (i, k, v) => setTransformers(t => t.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const addElecEquip = () => setElecEquip(e => [...e, { equipment: '', specs: '', qty: '', watts: '', hrsDay: '', daysYear: '' }]);
  const updateElecEquip = (i, k, v) => setElecEquip(e => e.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const addThermalEquip = () => setThermalEquip(e => [...e, { equipment: '', specs: '', qty: '', watts: '', hrsDay: '', daysYear: '' }]);
  const updateThermalEquip = (i, k, v) => setThermalEquip(e => e.map((x, j) => j === i ? { ...x, [k]: v } : x));

  const steps = ['Organization', 'Team', 'Electricity Bills', 'Thermal Energy', 'Transformers', 'Electrical Equipment', 'Thermal Equipment', 'Past Audits & Concerns', 'Declaration'];

  const submit = async () => {
    setBusy(true);
    try {
      await submitEnergyAudit({ org, team, elecBills, elecRate, thermalBills, thermalRate, transformers, elecEquip, thermalEquip, pastAudit, savingProjects, futureAudit, concerns, declaration });
      setStatus('ok');
    } catch { setStatus('err'); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="energy-audit-modal">
      <div className="absolute inset-0 bg-[var(--ink)]/60 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative bg-white rounded-3xl w-full max-w-[800px] max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[var(--blue)]/10 flex items-center justify-center">
              <Activity className="w-5 h-5 text-[var(--blue)]"/>
            </div>
            <div>
              <div className="font-display font-bold text-[18px]">Energy Pre-Audit Questionnaire</div>
              <div className="text-[12px] text-[var(--text-mid)]">Step {step + 1} of {steps.length} — {steps[step]}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full border border-[var(--line)] flex items-center justify-center hover:bg-[var(--cream)]" data-testid="energy-modal-close"><X className="w-4 h-4"/></button>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4">
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-[var(--blue)]' : 'bg-[var(--line)]'}`}/>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {status === 'ok' ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="w-16 h-16 text-[var(--blue)] mb-4"/>
              <div className="font-display font-bold text-[24px] mb-2">Audit Request Submitted!</div>
              <p className="text-[var(--text-mid)] max-w-md">Thank you — your energy pre-audit questionnaire has been submitted. Our team will review and get in touch shortly.</p>
              <button onClick={onClose} className="mt-6 px-6 py-3 rounded-full bg-[var(--blue)] text-white font-medium">Close</button>
            </div>
          ) : status === 'err' ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mb-4"/>
              <div className="font-display font-bold text-[24px] mb-2">Submission Failed</div>
              <p className="text-[var(--text-mid)]">Please try again or email solutions@tricoliteenergy.in directly.</p>
              <button onClick={() => setStatus(null)} className="mt-6 px-6 py-3 rounded-full bg-[var(--ink)] text-white font-medium">Try Again</button>
            </div>
          ) : (
            <>
              {step === 0 && (
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--blue)]">Organization Details</h4>
                  <FI label="Organisation Name *" value={org.name} onChange={v => setOrg(o => ({ ...o, name: v }))} placeholder="e.g. ABC Industries"/>
                  <FI label="Location (City) *" value={org.city} onChange={v => setOrg(o => ({ ...o, city: v }))} placeholder="e.g. Gurugram"/>
                  <div className="grid grid-cols-2 gap-3">
                    <FI label="Latitude" value={org.lat} onChange={v => setOrg(o => ({ ...o, lat: v }))} placeholder="28.4595"/>
                    <FI label="Longitude" value={org.lng} onChange={v => setOrg(o => ({ ...o, lng: v }))} placeholder="77.0266"/>
                  </div>
                  <FI label="Products" value={org.products} onChange={v => setOrg(o => ({ ...o, products: v }))} placeholder="Products manufactured or services"/>
                  <div className="grid grid-cols-2 gap-3">
                    <FI label="Plant Capacity" value={org.plantCapacity} onChange={v => setOrg(o => ({ ...o, plantCapacity: v }))} placeholder="e.g. 5000 units/day"/>
                    <FI label="Sanctioned Demand" value={org.sanctionedDemand} onChange={v => setOrg(o => ({ ...o, sanctionedDemand: v }))} placeholder="KVA"/>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FI label="Connected Load" value={org.connectedLoad} onChange={v => setOrg(o => ({ ...o, connectedLoad: v }))} placeholder="KW"/>
                    <FI label="Peak Demand (Registered)" value={org.peakDemand} onChange={v => setOrg(o => ({ ...o, peakDemand: v }))} placeholder="KW"/>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FI label="Lighting Load" value={org.lightingLoad} onChange={v => setOrg(o => ({ ...o, lightingLoad: v }))} placeholder="KW"/>
                    <FI label="Lighting Voltage" value={org.lightingVoltage} onChange={v => setOrg(o => ({ ...o, lightingVoltage: v }))} placeholder="V"/>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Sel label="Separate Lighting Feeder?" value={org.separateFeeder} onChange={v => setOrg(o => ({ ...o, separateFeeder: v }))}/>
                    <Sel label="APFC Panel Installed?" value={org.apfcInstalled} onChange={v => setOrg(o => ({ ...o, apfcInstalled: v }))}/>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--blue)]">Team Members</h4>
                    <button onClick={addTeam} className="text-[13px] text-[var(--blue)] font-semibold hover:underline">+ Add Member</button>
                  </div>
                  {team.map((t, i) => (
                    <div key={i} className="p-4 rounded-xl border border-[var(--line)] bg-[var(--cream)] space-y-3">
                      <FI label="Name" value={t.name} onChange={v => updateTeam(i, 'name', v)} placeholder="Full name"/>
                      <div className="grid grid-cols-3 gap-3">
                        <FI label="Designation" value={t.designation} onChange={v => updateTeam(i, 'designation', v)} placeholder="Title"/>
                        <FI label="Mobile" value={t.mobile} onChange={v => updateTeam(i, 'mobile', v)} placeholder="+91"/>
                        <FI label="Email" value={t.email} onChange={v => updateTeam(i, 'email', v)} placeholder="email@co.in"/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--blue)]">Electricity Bill Details</h4>
                  <div className="border border-[var(--line)] rounded-xl overflow-hidden">
                    <div className="grid grid-cols-[120px_1fr_1fr_1fr_1fr] bg-[var(--cream-deep)] text-[10px] uppercase tracking-wider font-semibold text-[var(--text-light)] p-2.5 gap-1.5">
                      <div>Month</div><div>Power Factor</div><div>KW Purchased</div><div>KW Generated</div><div>Consumption</div>
                    </div>
                    {elecBills.map((b, i) => (
                      <div key={i} className={`grid grid-cols-[120px_1fr_1fr_1fr_1fr] p-2 gap-1.5 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[var(--cream)]'}`}>
                        <div className="text-[12px] font-medium">{b.month}</div>
                        {['pf','kwPurchased','kwGenerated','consumption'].map(k => (
                          <input key={k} value={b[k]} onChange={e => updateElecBill(i, k, e.target.value)} className="text-[12px] border border-[var(--line)] rounded-lg px-2 py-1.5 w-full" placeholder="—"/>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FI label="Rate / kWh" value={elecRate.ratePerKwh} onChange={v => setElecRate(r => ({ ...r, ratePerKwh: v }))} placeholder="₹"/>
                    <FI label="Total Annual Cost" value={elecRate.totalAnnualCost} onChange={v => setElecRate(r => ({ ...r, totalAnnualCost: v }))} placeholder="₹"/>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--blue)]">Thermal Energy</h4>
                  <div className="border border-[var(--line)] rounded-xl overflow-hidden">
                    <div className="grid grid-cols-[120px_70px_1fr_1fr_1fr] bg-[var(--cream-deep)] text-[10px] uppercase tracking-wider font-semibold text-[var(--text-light)] p-2.5 gap-1.5">
                      <div>Month</div><div>Year</div><div>Fuel (Power)</div><div>Fuel (Other)</div><div>Consumption</div>
                    </div>
                    {thermalBills.map((b, i) => (
                      <div key={i} className={`grid grid-cols-[120px_70px_1fr_1fr_1fr] p-2 gap-1.5 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[var(--cream)]'}`}>
                        <div className="text-[12px] font-medium">{b.month}</div>
                        {['year','fuelPower','fuelOther','consumption'].map(k => (
                          <input key={k} value={b[k]} onChange={e => updateThermalBill(i, k, e.target.value)} className="text-[12px] border border-[var(--line)] rounded-lg px-2 py-1.5 w-full" placeholder="—"/>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FI label="Rate / kWh" value={thermalRate.ratePerKwh} onChange={v => setThermalRate(r => ({ ...r, ratePerKwh: v }))} placeholder="₹"/>
                    <FI label="Total Annual Cost" value={thermalRate.totalAnnualCost} onChange={v => setThermalRate(r => ({ ...r, totalAnnualCost: v }))} placeholder="₹"/>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--blue)]">Installed Transformer Details</h4>
                    <button onClick={addTransformer} className="text-[13px] text-[var(--blue)] font-semibold hover:underline">+ Add</button>
                  </div>
                  {transformers.map((t, i) => (
                    <div key={i} className="grid grid-cols-4 gap-3 p-3 rounded-xl border border-[var(--line)] bg-[var(--cream)]">
                      <FI label="Make" value={t.make} onChange={v => updateTransformer(i, 'make', v)} placeholder="Brand"/>
                      <FI label="Type" value={t.type} onChange={v => updateTransformer(i, 'type', v)} placeholder="Type"/>
                      <FI label="KVA Rating" value={t.kvaRating} onChange={v => updateTransformer(i, 'kvaRating', v)} placeholder="KVA"/>
                      <FI label="% Loading" value={t.loading} onChange={v => updateTransformer(i, 'loading', v)} placeholder="%"/>
                    </div>
                  ))}
                </div>
              )}

              {step === 5 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--blue)]">Major Electrical Equipment</h4>
                    <button onClick={addElecEquip} className="text-[13px] text-[var(--blue)] font-semibold hover:underline">+ Add</button>
                  </div>
                  {elecEquip.map((e, i) => (
                    <div key={i} className="p-3 rounded-xl border border-[var(--line)] bg-[var(--cream)] space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <FI label="Equipment" value={e.equipment} onChange={v => updateElecEquip(i, 'equipment', v)} placeholder="Name"/>
                        <FI label="Make / Specs" value={e.specs} onChange={v => updateElecEquip(i, 'specs', v)} placeholder="Details"/>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        <FI label="Qty" value={e.qty} onChange={v => updateElecEquip(i, 'qty', v)} placeholder="#"/>
                        <FI label="Watts" value={e.watts} onChange={v => updateElecEquip(i, 'watts', v)} placeholder="W"/>
                        <FI label="Hrs/Day" value={e.hrsDay} onChange={v => updateElecEquip(i, 'hrsDay', v)} placeholder="hrs"/>
                        <FI label="Days/Year" value={e.daysYear} onChange={v => updateElecEquip(i, 'daysYear', v)} placeholder="days"/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {step === 6 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--blue)]">Major Thermal Equipment</h4>
                    <button onClick={addThermalEquip} className="text-[13px] text-[var(--blue)] font-semibold hover:underline">+ Add</button>
                  </div>
                  {thermalEquip.map((e, i) => (
                    <div key={i} className="p-3 rounded-xl border border-[var(--line)] bg-[var(--cream)] space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <FI label="Equipment" value={e.equipment} onChange={v => updateThermalEquip(i, 'equipment', v)} placeholder="Name"/>
                        <FI label="Make / Specs" value={e.specs} onChange={v => updateThermalEquip(i, 'specs', v)} placeholder="Details"/>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        <FI label="Qty" value={e.qty} onChange={v => updateThermalEquip(i, 'qty', v)} placeholder="#"/>
                        <FI label="Watts" value={e.watts} onChange={v => updateThermalEquip(i, 'watts', v)} placeholder="W"/>
                        <FI label="Hrs/Day" value={e.hrsDay} onChange={v => updateThermalEquip(i, 'hrsDay', v)} placeholder="hrs"/>
                        <FI label="Days/Year" value={e.daysYear} onChange={v => updateThermalEquip(i, 'daysYear', v)} placeholder="days"/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {step === 7 && (
                <div className="space-y-5">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--blue)]">Past Audits & Concerns</h4>
                  <Sel label="Energy Audit carried out before?" value={pastAudit.everDone} onChange={v => setPastAudit(p => ({ ...p, everDone: v }))}/>
                  {pastAudit.everDone === 'Yes' && pastAudit.audits.map((a, i) => (
                    <div key={i} className="grid grid-cols-3 gap-3 p-3 rounded-xl border border-[var(--line)] bg-[var(--cream)]">
                      <FI label="Year" value={a.year} onChange={v => { const n = [...pastAudit.audits]; n[i] = { ...n[i], year: v }; setPastAudit(p => ({ ...p, audits: n })); }} placeholder="Year"/>
                      <FI label="Type" value={a.type} onChange={v => { const n = [...pastAudit.audits]; n[i] = { ...n[i], type: v }; setPastAudit(p => ({ ...p, audits: n })); }} placeholder="Pre/Detailed/Walk"/>
                      <FI label="Agency" value={a.agency} onChange={v => { const n = [...pastAudit.audits]; n[i] = { ...n[i], agency: v }; setPastAudit(p => ({ ...p, audits: n })); }} placeholder="Agency name"/>
                    </div>
                  ))}
                  <h4 className="font-display font-bold text-[14px] mt-2">Preferred Dates for Detailed Audit</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <FI label="Year" value={futureAudit.year} onChange={v => setFutureAudit(f => ({ ...f, year: v }))} placeholder="2025"/>
                    <FI label="Month" value={futureAudit.month} onChange={v => setFutureAudit(f => ({ ...f, month: v }))} placeholder="March"/>
                    <FI label="Preferred Dates" value={futureAudit.dates} onChange={v => setFutureAudit(f => ({ ...f, dates: v }))} placeholder="1st-5th"/>
                  </div>
                  <div>
                    <label className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-1.5 font-medium">Key Customer Concern on Energy Wastage</label>
                    <textarea value={concerns} onChange={e => setConcerns(e.target.value)} className="cf-textarea" placeholder="Describe concerns about energy wastage..."/>
                  </div>
                </div>
              )}

              {step === 8 && (
                <div className="space-y-4">
                  <h4 className="font-display font-bold text-[16px] uppercase tracking-wider text-[var(--blue)]">Declaration</h4>
                  <FI label="Date *" value={declaration.date} onChange={v => setDeclaration(d => ({ ...d, date: v }))} type="date" placeholder="DD/MM/YYYY"/>
                  <FI label="Name *" value={declaration.name} onChange={v => setDeclaration(d => ({ ...d, name: v }))} placeholder="Your full name"/>
                  <FI label="Designation" value={declaration.designation} onChange={v => setDeclaration(d => ({ ...d, designation: v }))} placeholder="Your designation"/>
                  <div className="mt-4 p-4 rounded-xl bg-[var(--cream-deep)] border border-[var(--line)] text-[13px] text-[var(--text-mid)] italic">
                    "I hereby certify this information collected to be true, to the best of my available knowledge as on the date mentioned above."
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!status && (
          <div className="flex items-center justify-between p-6 border-t border-[var(--line)]">
            <button onClick={() => step > 0 && setStep(s => s - 1)} disabled={step === 0} className="px-5 py-2.5 rounded-full border border-[var(--line)] text-[14px] font-medium disabled:opacity-30 hover:bg-[var(--cream)] transition-colors">Back</button>
            {step < steps.length - 1 ? (
              <button onClick={() => setStep(s => s + 1)} className="px-6 py-2.5 rounded-full bg-[var(--blue)] text-white text-[14px] font-medium hover:bg-[var(--blue-soft)] transition-colors" data-testid="energy-next-btn">
                Next <ArrowUpRight className="w-3.5 h-3.5 inline ml-1"/>
              </button>
            ) : (
              <button onClick={submit} disabled={busy} className="px-6 py-2.5 rounded-full bg-[var(--blue)] text-white text-[14px] font-medium hover:bg-[var(--blue-soft)] transition-colors disabled:opacity-60" data-testid="energy-submit-btn">
                {busy ? 'Submitting…' : 'Submit Audit Request'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FI({ label, value, onChange, placeholder, type }) {
  return (
    <label className="block">
      <span className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-1.5 font-medium">{label}</span>
      <input type={type || 'text'} value={value} onChange={e => onChange(e.target.value)} className="cf-input" placeholder={placeholder}/>
    </label>
  );
}

function Sel({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[12px] uppercase tracking-wider text-[var(--text-light)] mb-1.5 font-medium">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} className="cf-input">
        <option value="No">No</option><option value="Yes">Yes</option>
      </select>
    </label>
  );
}
